import { Component, ComponentInterface, Event, EventEmitter, h, Host, Method, Prop, Watch } from "@stencil/core";

let id = 0;

/**
 * @slot (default) - The content of the details panel.
 * @slot summary - The content shown as header.
 * @slot summary-icon - The icon next to the summary.
 */
@Component({
    tag: "uic-accordion-item",
    styleUrl: "accordion-item.scss"
})
export class AccordionItem implements ComponentInterface {
    private headerId = `uic-accordion-item-header-${++id}`;
    private contentId = `uic-accordion-item-content-${id}`;

    private headerElement: HTMLElement;

    /**
     * The heading level of the header element.
     */
    @Prop() readonly headingLevel: 1 | 2 | 3 | 4 | 5 | 6 = 3;

    /**
     * The summary to show in the accordion header.
     */
    @Prop() readonly summary: string;

    /**
     * If set to `true`, the accordions details are visible.
     */
    @Prop({ reflect: true, mutable: true }) open = false;

    /**
     * If set to `true`, the user cannot interact with the accordion.
     */
    @Prop({ reflect: true }) readonly disabled: boolean = false;

    /**
     * Emitted when the accordion shows details.
     */
    @Event() uicShow: EventEmitter;

    /**
     * Emitted when the accordion hides details.
     */
    @Event() uicHide: EventEmitter;

    @Watch("open")
    handleOpenChanged(open) {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        open ? this.uicShow.emit() : this.uicHide.emit();
    }

    /**
     * Set focus to the header.
     */
    @Method()
    async focusHeader() {
        this.headerElement?.focus();
    }

    private handleHeaderClick() {
        this.open = !this.open;
    }

    render() {
        return (
            <Host
                class={{
                    "uic-accordion-item": true,
                    "uic-accordion-item--open": this.open,
                    "uic-accordion-item--disabled": this.disabled
                }}
            >
                <div role='heading' aria-level={this.headingLevel}>
                    <button
                        class='uic-accordion-item__header'
                        type='button'
                        disabled={this.disabled}
                        aria-expanded={this.open ? "true" : "false"}
                        aria-controls={this.contentId}
                        ref={(el) => (this.headerElement = el)}
                        onClick={() => this.handleHeaderClick()}
                    >
                        <span class='uic-accordion-item__summary-icon'>
                            <slot name='summary-icon'>
                                <uic-icon class='uic-accordion-item__chevron' name='uic:chevron-down'></uic-icon>
                            </slot>
                        </span>

                        <span class='uic-accordion-item__summary' id={this.headerId}>
                            <slot name='summary'>{this.summary}</slot>
                        </span>
                    </button>
                </div>
                <div role='region' id={this.contentId} aria-labelledby={this.headerId} class='uic-accordion-item__content'>
                    <slot></slot>
                </div>
            </Host>
        );
    }
}
